// playLocalAlbum — small helper used by the album grid double-click and the
// right-click context menu. Loads the album's tracks lazily, formats them for
// the queue, and routes through QueueController (the one canonical play path).
//
// Lives outside the React tree on purpose so other surfaces (radio "from
// album" button, drag-to-queue, etc.) can reuse it later without prop-drill.

import { buildLocalCoverUrl } from '../lib/connector';
import { libraryService } from '../services/libraryService';
import {
  isProvisionalAlbum,
  isProvisionalCatalogActionBlocked,
  notifyProvisionalCatalogActionBlocked,
} from '../services/catalogBootSnapshot';
import { QueueController } from '../transport/QueueController';
import { diagnosticLog } from './diagnosticLogger';

export function buildLocalQueueTrack(t, i, albumData) {
  const rate =
    t.sample_rate ||
    (t.maximum_sampling_rate ? Math.round(Number(t.maximum_sampling_rate) * 1000) : null) ||
    albumData?.sample_rate ||
    null;
  const depth = t.bit_depth || t.maximum_bit_depth || albumData?.bit_depth || null;
  const cover =
    buildLocalCoverUrl(t.embedded_cover_hash || albumData?.embedded_cover_hash, { thumb: true })
    || buildLocalCoverUrl(t.folder_cover_hash || albumData?.folder_cover_hash, { thumb: true })
    || t.cover_art_url
    || t.artworkUrl
    || albumData?.cover_art_url
    || albumData?.artworkUrl
    || '';
  return {
    id: t.id || `local_${i}`,
    title: t.title || 'Unknown',
    artist: t.artistName || t.artist || albumData?.artistName || albumData?.artist_name || '',
    album: albumData?.title || '',
    albumId: albumData?.album_key || albumData?.id || null,
    album_key: albumData?.album_key || albumData?.id || null,
    artworkUrl: cover,
    artUrl: cover,
    embedded_cover_hash: t.embedded_cover_hash || albumData?.embedded_cover_hash || null,
    folder_cover_hash: t.folder_cover_hash || albumData?.folder_cover_hash || null,
    duration: t.duration || 0,
    source: 'local_library',
    file_path: t.file_path,
    machine_id: t.machine_id || albumData?.machine_id || null,
    _source_tunnel_url: t._source_tunnel_url || albumData?._source_tunnel_url || null,
    format: String(t.format || albumData?.format || '').toUpperCase(),
    sample_rate: rate,
    bit_depth: depth,
    channels: t.channels ?? null,
  };
}

/**
 * Resolve an album row → fetch tracks → push to QueueController.
 *
 *   `mode` controls what the queue does after fetching:
 *     - 'play'    → play from index 0 (default — double-click behavior)
 *     - 'append'  → add to the end of the queue (context menu)
 *
 *   `source` identifies the UI surface for fleet playback tracing.
 */
export async function playLocalAlbumFromGrid(album, { mode = 'play', source = 'grid-double-click' } = {}) {
  if (!album) return;
  const key = album.album_key || album.id;
  if (!key) {
    console.warn('[playLocalAlbumFromGrid] album has no album_key/id', album);
    return;
  }

  if (isProvisionalAlbum(album)) {
    notifyProvisionalCatalogActionBlocked();
    return;
  }

  console.log('[playLocalAlbumFromGrid]', mode, '←', source, '—', album?.title || key);
  diagnosticLog('info', 'playback', 'playLocalAlbumFromGrid entry', {
    album_key: key,
    source,
    mode,
  });

  try {
    const resp = await libraryService.getAlbum(key, {
      machineId: album.machine_id || undefined,
    });
    const tracks = resp?.tracks || [];
    const withFilePath = tracks.filter((t) => !!t?.file_path).length;
    diagnosticLog('info', 'playback', 'playLocalAlbumFromGrid getAlbum result', {
      ok: tracks.length > 0,
      track_count: tracks.length,
      tracks_with_file_path: withFilePath,
      reason: tracks.length ? undefined : 'no_tracks',
    });
    if (!tracks.length) {
      console.warn('[playLocalAlbumFromGrid] album has no tracks', key);
      return;
    }
    const albumData = resp?.album || album;
    const formatted = tracks.map((t, i) => buildLocalQueueTrack(t, i, {
      ...albumData,
      machine_id: albumData?.machine_id || album?.machine_id || null,
      _source_tunnel_url: albumData?._source_tunnel_url || album?._source_tunnel_url || null,
    }));
    if (mode === 'append') {
      QueueController.appendToQueue(formatted);
    } else {
      QueueController.playFrom(formatted, 0);
    }
  } catch (err) {
    if (isProvisionalCatalogActionBlocked(err)) {
      notifyProvisionalCatalogActionBlocked(err);
      return;
    }
    diagnosticLog('info', 'playback', 'playLocalAlbumFromGrid getAlbum result', {
      ok: false,
      track_count: 0,
      reason: String(err?.message || err || 'fetch_failed').slice(0, 120),
    });
    console.error('[playLocalAlbumFromGrid] failed', err);
  }
}
